import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";

import { api } from "@/api/client";
import type {
  DeleteSessionResponse,
  SessionDetailResponse,
  SessionListItem,
  TurnDetail,
} from "@/api/types";
import { FlagIcon, ThumbDownIcon, ThumbUpIcon } from "@/components/Icons";
import { PanelHelp } from "@/components/PanelHelp";
import { DeleteSessionCard, deletionSummary, UserLookup } from "@/components/SessionErasure";
import { Staleness } from "@/components/Staleness";
import { usePoll } from "@/hooks/usePoll";
import { t } from "@/i18n";
import { formatEventMetadata } from "@/lib/eventMeta";
import { downloadSessionCsv } from "@/lib/export";
import { formatDateTime, formatDuration, formatInt, modeLabel } from "@/lib/format";
import { depthLabel, depthValue } from "@/lib/reasoningDepth";
import { renderAssistantMarkdown } from "@/lib/renderMarkdown";
import { httpStatus } from "@/lib/runtimeChange";
import { includeTestSessions, inspectSessionId } from "@/state/filters";

export function SessionInspectorView() {
  const list = usePoll(
    (s) =>
      api.recentSessions(
        { limit: 50, includeTest: includeTestSessions.value },
        s,
      ),
    [includeTestSessions.value],
  );

  const [sessionId, setSessionId] = useState<string>("");
  const [detail, setDetail] = useState<SessionDetailResponse | null>(null);
  const [detailError, setDetailError] = useState<string>("");
  // The id names no stored session (a 404): deleted or expired. An erasure
  // that stopped after the document left turns and events behind, which the
  // delete card removes on a repeat.
  const [gone, setGone] = useState(false);
  // "Widget-Ansicht": render assistant answers like the widget does
  // (markdown + sanitized SVG) so broken graphics/format are verifiable.
  const [renderAnswers, setRenderAnswers] = useState(true);
  // Right to erasure: the hash handed to the login lookup, the last delete's
  // outcome, and a counter that re-runs the lookup after a delete.
  const [lookupSeed, setLookupSeed] = useState("");
  const [deletedNote, setDeletedNote] = useState("");
  const [deletions, setDeletions] = useState(0);

  // Cross-view jump from the Events Stream: load the requested session, then
  // clear the signal so a later manual lookup isn't overridden on re-entry.
  useEffect(() => {
    const target = inspectSessionId.value;
    if (target) {
      setSessionId(target);
      inspectSessionId.value = "";
    }
  }, [inspectSessionId.value]);

  useEffect(() => {
    if (!sessionId) {
      setDetail(null);
      setGone(false);
      return;
    }
    const ctl = new AbortController();
    api
      .sessionDetail(sessionId, ctl.signal)
      .then((d) => {
        if (!ctl.signal.aborted) {
          setDetail(d);
          setDetailError("");
          setGone(false);
        }
      })
      .catch((e: Error) => {
        if (!ctl.signal.aborted) {
          setDetail(null);
          const notFound = httpStatus(e) === 404;
          setGone(notFound);
          setDetailError(notFound ? "" : e.message);
        }
      });
    return () => ctl.abort();
  }, [sessionId]);

  const afterDelete = (result: DeleteSessionResponse) => {
    setDeletedNote(deletionSummary(result));
    setSessionId("");
    setDeletions((n) => n + 1);
    list.refetch();
  };

  return (
    <div style="display: grid; grid-template-columns: 320px 1fr; gap: 24px;">
      <aside class="card" style="max-height: 80vh; overflow-y: auto;">
        <h3>{t("sessions.recent")}</h3>
        <Staleness
          lastFetched={list.lastFetched}
          loading={list.loading}
          error={list.error}
          onRefresh={list.refetch}
        />
        <table class="table">
          <thead>
            <tr>
              <th>{t("sessions.col.when")}</th>
              <th>{t("sessions.col.mode")}</th>
              <th>{t("sessions.col.q")}</th>
              <th>{t("sessions.col.tags")}</th>
            </tr>
          </thead>
          <tbody>
            {list.data?.sessions.map((s) => (
              <tr
                key={s.session_id}
                onClick={() => setSessionId(s.session_id)}
                style={`cursor: pointer; ${s.session_id === sessionId ? "background: #f7f9fd;" : ""}`}
              >
                <td class="muted">{formatDateTime(s.created_at)}</td>
                <td>{modeLabel(s.chat_mode)}</td>
                <td>{formatInt(s.question_count)}</td>
                <td>
                  <SessionTags session={s} />
                </td>
              </tr>
            ))}
            {(!list.data || list.data.sessions.length === 0) && (
              <tr>
                <td colspan={4} class="muted">
                  {t("sessions.noneRecent")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </aside>

      <section style="display: grid; gap: 24px;">
        <div class="card">
          <h2>{t("sessions.lookup")}</h2>
          <PanelHelp k="sessions.overview" />
          <div style="display: flex; gap: 8px;">
            <input
              type="text"
              value={sessionId}
              onInput={(e) => setSessionId((e.target as HTMLInputElement).value)}
              placeholder={t("sessions.placeholder")}
              style="flex: 1; padding: 8px 12px; border: 1px solid var(--color-card-border); border-radius: var(--radius-s); font-family: var(--font-mono); font-size: 13px;"
            />
            <button
              class="window-pill"
              onClick={() => {
                if (sessionId) {
                  setSessionId("");
                  setTimeout(() => setSessionId(sessionId), 0);
                }
              }}
            >
              {t("sessions.reload")}
            </button>
          </div>
          {detailError && (
            <div class="error-banner" style="margin-top: 12px;">
              {detailError}
            </div>
          )}
          {deletedNote && (
            <div class="muted" role="status" style="margin-top: 12px;">
              {deletedNote}
            </div>
          )}
        </div>

        <UserLookup
          seed={lookupSeed}
          refresh={deletions}
          onOpen={(id) => {
            setDeletedNote("");
            setSessionId(id);
          }}
        />

        {detail && (
          <>
            <div class="card">
              <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px;">
                <h2 style="margin: 0;">{t("sessions.metadata")}</h2>
                <button
                  class="window-pill"
                  title={t("sessions.exportTitle")}
                  onClick={() => downloadSessionCsv(detail)}
                >
                  {t("sessions.export")}
                </button>
              </div>
              <SessionMeta detail={detail} onLookup={setLookupSeed} />
            </div>

            <div class="card">
              <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px;">
                <h2 style="margin: 0;">{t("sessions.turns", { n: detail.turns.length })}</h2>
                <label class="toggle" title={t("sessions.widgetViewTitle")}>
                  <input
                    type="checkbox"
                    checked={renderAnswers}
                    onInput={(e) =>
                      setRenderAnswers((e.target as HTMLInputElement).checked)
                    }
                  />
                  <span>{t("sessions.widgetView")}</span>
                </label>
              </div>
              <p class="muted" style="font-size: 12px; margin: 4px 0 8px;">
                {t("sessions.retrievalLegend")}
              </p>
              {detail.turns.map((turn, i) => (
                <details
                  key={turn.turn_id}
                  open={i < 3}
                  style="border-bottom: 1px solid var(--color-divider); padding: 12px 0;"
                >
                  <summary style="cursor: pointer;">
                    <span class="hl-blue">#{i + 1}</span>{" "}
                    <span class="muted">{formatDateTime(turn.timestamp)}</span> ·{" "}
                    {modeLabel(turn.chat_mode)} · <span class="muted">{turn.model_used}</span>
                    {depthSummary(turn) && (
                      <span class="muted" title={t("sessions.depthTitle")}>
                        {" · "}
                        {depthSummary(turn)}
                      </span>
                    )}
                    {turn.guardrail_triggered && (
                      <span class="pill degraded" style="margin-left: 8px;">
                        {t("sessions.guardrail", {
                          reason: turn.guardrail_reason ?? t("sessions.guardrailYes"),
                        })}
                      </span>
                    )}
                    {!turn.streaming_completed && (
                      <span class="pill down" style="margin-left: 8px;">
                        {t("sessions.midStreamFail")}
                      </span>
                    )}
                  </summary>
                  <div style="margin-top: 8px; display: grid; gap: 8px;">
                    <div>
                      <div class="muted" style="font-size: 12px;">{t("sessions.user")}</div>
                      <div>{turn.user_query}</div>
                    </div>
                    <div>
                      <div class="muted" style="font-size: 12px;">{t("sessions.assistant")}</div>
                      {renderAnswers ? (
                        <div
                          class="rendered-answer"
                          // Sanitized by renderAssistantMarkdown — the same
                          // DOMPurify whitelist as the widget's MessageBubble.
                          dangerouslySetInnerHTML={{
                            __html: renderAssistantMarkdown(turn.assistant_response),
                          }}
                        />
                      ) : (
                        <div style="white-space: pre-wrap;">{turn.assistant_response}</div>
                      )}
                    </div>
                    <div class="muted" style="font-size: 12px;">
                      {t("sessions.tokens", { in: turn.input_tokens, out: turn.output_tokens })}
                      {turn.cache_hit && t("sessions.cacheHit")}
                      {" · "}
                      {t("sessions.intent", { v: turn.question_intent })}
                      {turn.berufsfeld && t("sessions.berufsfeld", { v: turn.berufsfeld })}
                    </div>
                    {retrievalSummary(turn) && (
                      <div class="muted" style="font-size: 12px;" title={t("sessions.retrievalTitle")}>
                        {retrievalSummary(turn)}
                      </div>
                    )}
                    {(turn.retrieval_topic || turn.search_query) && (
                      <div
                        class="muted"
                        style="font-size: 12px; word-break: break-word;"
                        title={t("sessions.retrievalContextTitle")}
                      >
                        {turn.retrieval_topic &&
                          t("sessions.retrievalTopic", { v: turn.retrieval_topic })}
                        {turn.retrieval_topic && turn.search_query && " · "}
                        {turn.search_query && t("sessions.searchQuery", { v: turn.search_query })}
                      </div>
                    )}
                    {latencySummary(turn) && (
                      <div class="muted" style="font-size: 12px;" title={t("sessions.latencyTitle")}>
                        {latencySummary(turn)}
                      </div>
                    )}
                    {turn.retrieved_chunks.length > 0 && (
                      <div class="muted" style="font-size: 12px;">
                        {t("sessions.sources", { n: turn.retrieved_chunks.length })}
                        <ul style="margin: 4px 0; padding-left: 16px;">
                          {turn.retrieved_chunks.slice(0, 8).map((c, j) => (
                            <li key={j} style="word-break: break-all;">
                              {sourceLine(c, j)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </details>
              ))}
            </div>

            <div class="card">
              <h2>{t("sessions.events", { n: detail.events.length })}</h2>
              <table class="table">
                <thead>
                  <tr>
                    <th>{t("common.when")}</th>
                    <th>{t("common.type")}</th>
                    <th>{t("common.metadata")}</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.events.map((e) => (
                    <tr key={e.event_id}>
                      <td class="muted">{formatDateTime(e.timestamp)}</td>
                      <td>{e.event_type}</td>
                      <td
                        class="muted"
                        style="font-size: 12px;"
                        title={
                          Object.keys(e.metadata).length === 0
                            ? undefined
                            : JSON.stringify(e.metadata)
                        }
                      >
                        {formatEventMetadata(e.event_type, e.metadata)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <DeleteSessionCard sessionId={detail.session.session_id} onDeleted={afterDelete} />
          </>
        )}
        {!detail && gone && (
          <>
            <p class="muted" role="status">
              {t("sessions.erasure.goneHint")}
            </p>
            <DeleteSessionCard sessionId={sessionId} onDeleted={afterDelete} />
          </>
        )}
        {!detail && !detailError && !gone && (
          <div class="muted">{t("sessions.selectHint")}</div>
        )}
      </section>
    </div>
  );
}

function SessionMeta({
  detail,
  onLookup,
}: {
  detail: SessionDetailResponse;
  onLookup: (userHash: string) => void;
}) {
  const s = detail.session;
  const durationMs =
    new Date(s.updated_at).getTime() - new Date(s.created_at).getTime();
  const rows: Array<[string, string]> = [
    [t("sessions.meta.id"), s.session_id],
    [t("sessions.meta.created"), formatDateTime(s.created_at)],
    [t("sessions.meta.updated"), formatDateTime(s.updated_at)],
    [t("sessions.meta.duration"), formatDuration(durationMs)],
    [t("sessions.meta.locale"), s.locale],
    [t("sessions.meta.country"), s.country],
    [t("sessions.meta.device"), s.device],
    [t("sessions.meta.mode"), modeLabel(s.chat_mode)],
    [t("sessions.meta.startedModes"), s.started_modes.map(modeLabel).join(" → ")],
    [t("sessions.meta.turns"), formatInt(s.turn_count)],
    [t("sessions.meta.questions"), formatInt(s.question_count)],
    [t("sessions.meta.isTest"), s.is_test ? t("common.yes") : t("common.no")],
    [t("sessions.meta.title"), s.title ?? "—"],
  ];
  return (
    <table class="table">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}>
            <td class="muted" style="width: 180px;">{k}</td>
            <td style="font-family: var(--font-mono); font-size: 13px; word-break: break-all;">
              {v}
            </td>
          </tr>
        ))}
        {detail.user_pseudonym && (
          <tr>
            <td class="muted" style="width: 180px;">{t("sessions.meta.userHash")}</td>
            <td style="font-family: var(--font-mono); font-size: 13px; word-break: break-all;">
              {detail.user_pseudonym}{" "}
              <button class="window-pill" onClick={() => onLookup(detail.user_pseudonym)}>
                {t("sessions.meta.userHashLookup")}
              </button>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

// One-line retrieval funnel of a turn: what the search found, what reached the
// model, what the answer cited, what the score floor removed, how much text
// the model read. Empty for turns without retrieval (Multicheck, no-search
// turns) and for turns stored before the funnel fields existed when they also
// forwarded nothing. Technical vocabulary, identical in both languages.
// Exported for unit tests.
export function retrievalSummary(turn: TurnDetail): string {
  const found = turn.docs_returned ?? 0;
  const forwarded = turn.chunks_used;
  if (found === 0 && forwarded === 0) {
    return "";
  }
  const cited = turn.sources_cited ?? 0;
  const parts = [`retrieval: ${found} docs → ${forwarded} pages → ${cited} cited`];
  const floored = turn.docs_below_floor ?? 0;
  if (floored > 0) {
    parts.push(`${floored} below score floor`);
  }
  if (turn.context_chars !== undefined) {
    parts.push(`${formatInt(turn.context_chars)} chars`);
  }
  return parts.join(" · ");
}

// Time to first token and total duration of a turn, both measured by the
// streaming route (request start → first token → done). Empty for turns
// stored before the route measured them and for the no-answer path (both 0).
// Technical vocabulary, identical in both languages. Exported for unit tests.
export function latencySummary(turn: TurnDetail): string {
  const ttft = turn.ttft_ms ?? 0;
  const total = turn.total_latency_ms ?? 0;
  if (ttft === 0 && total === 0) {
    return "";
  }
  return `latency: ${formatInt(ttft)} ms to first token → ${formatInt(total)} ms total`;
}

// The reasoning depth the turn ran with, under the provider's own name for the
// knob (Thinking on Gemini, Effort on Claude). Empty for turns stored before
// the provider was recorded: without it the value cannot be read. Exported for
// unit tests.
export function depthSummary(turn: TurnDetail): string {
  if (!turn.provider) {
    return "";
  }
  const depth = { provider: turn.provider, effort: turn.reasoning_depth ?? "" };
  return `${depthLabel(depth)}: ${depthValue(depth)}`;
}

// "[n] url · score" per forwarded source, the marker matching the [n] the
// answer text uses. Reads the loosely-typed chunk dict defensively.
export function sourceLine(c: Record<string, unknown>, index: number): string {
  const url = typeof c.source_url === "string" && c.source_url ? c.source_url : "(no url)";
  const score = typeof c.relevance_score === "number" ? ` · ${c.relevance_score.toFixed(2)}` : "";
  return `[${index + 1}] ${url}${score}`;
}

// Review tags for one recent-sessions row: positive and negative ratings and
// flagged turns, so sessions worth reviewing stand out at a glance. All
// counts default to 0 (older backends omit the fields), so an untagged
// session renders an em dash. Exported for unit tests.
export function SessionTags({ session }: { session: SessionListItem }) {
  const positive = session.feedback_positive ?? 0;
  const negative = session.feedback_negative ?? 0;
  const flagged = session.flagged_count ?? 0;
  if (positive === 0 && negative === 0 && flagged === 0) {
    return <span class="muted">—</span>;
  }
  return (
    <span style="display: inline-flex; gap: 6px; white-space: nowrap;">
      {positive > 0 && (
        <TagBadge name={t("sessions.tags.positive", { n: positive })} count={positive}>
          <ThumbUpIcon />
        </TagBadge>
      )}
      {negative > 0 && (
        <TagBadge name={t("sessions.tags.negative", { n: negative })} count={negative}>
          <ThumbDownIcon />
        </TagBadge>
      )}
      {flagged > 0 && (
        <TagBadge
          name={
            flagged === 1
              ? t("sessions.tags.flagged", { n: flagged })
              : t("sessions.tags.flaggedPlural", { n: flagged })
          }
          count={flagged}
        >
          <FlagIcon />
        </TagBadge>
      )}
    </span>
  );
}

// An icon and its count, read as one: the tooltip's words name the badge.
function TagBadge({
  name,
  count,
  children,
}: {
  name: string;
  count: number;
  children: ComponentChildren;
}) {
  return (
    <span role="img" aria-label={name} title={name}>
      {children}
      {formatInt(count)}
    </span>
  );
}
